import React from "react";
import { ExternalLink, Pause, Play, Trash2, Settings2, LayoutDashboard, BarChart3, Package } from "lucide-react";
import { tenantDisplayName, tenantOwnerEmail } from "../tenantDisplay";
import { tenantSiteUrl } from "@/lib/tenantHost";
import { formatMoneyLedger } from "../../lib/formatMoney";
import { validBilling } from "../billingCharge";
import { practiceConsoleHref } from "../practiceConsole";
import { useScreenWrites } from "../screenWrites";

// Every chip's text meets WCAG AA (4.5:1) on its own fill: the -600 shades on -100 measured
// 2.9 to 3.9:1, and cancelled's slate-400 2.3:1 (TenantTableRow.statusContrast.test.js).
const statusBadge = (status) => {
  const colors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-500/30",
    pending: "bg-amber-100 text-amber-800 border-amber-500/30",
    suspended: "bg-rose-100 text-rose-700 border-rose-200",
    past_due: "bg-orange-100 text-orange-800 border-orange-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
    churned: "bg-slate-200 text-slate-700 border-slate-300",
  };
  return colors[status] || colors.cancelled;
};

// Why an unfinished deletion is stopped (D7 contract 3.6). Stopped because Solunary still owes
// the practice money (or a client payment to it is not settled), it waits for the payout
// (3.14): past its point of no return, only the retry once paid out finishes it (3.10), before
// it, abandoning is the other way out. Past its point of no return for another reason, only a
// retry finishes it. Otherwise Stripe did not confirm a cancellation.
const deletionHintKey = (deletion) => {
  const owed = deletion.last_error?.code === "PRACTICE_DELETE_OWED";
  if (deletion.committed) return owed ? "tl.deletionCommittedOwedHint" : "tl.deletionCommittedHint";
  if (owed) return "tl.deletionOwedHint";
  return "tl.deletionUnfinishedHint";
};

/** The cell of an action this row does not offer: the button's size (28px), nothing to
 *  read or focus, so the actions after it keep their grid cell. */
const EmptySlot = ({ slot }) => <span aria-hidden="true" data-slot={slot} className="block w-7 h-7" />;

export const TenantTableRow = ({ tn, t, lang, onPlanEdit, onEntitlementsEdit, onAct }) => {
  const billing = validBilling(tn.billing);
  const { manageAdmin } = useScreenWrites("tenants");
  const consoleHref = practiceConsoleHref(tn.slug);  // null: no slug, no console address
  // The column mixes currencies, so the price names its code (ledger style, S2 "Money"). The
  // practice's own currency then gets a line only when the price does not already say it.
  const showCurrency = !billing
    || String(billing.price.currency).toLowerCase() !== String(tn.currency || "").toLowerCase();
  return (
  <tr data-testid={`sa-tenant-${tn.slug}`} className="border-t border-slate-100 hover:bg-white">
    <td className="px-3 py-4">
      <div data-cell="name" className="font-medium truncate" title={tenantDisplayName(tn)}>{tenantDisplayName(tn)}</div>
      {tn.deletion && (
        <span
          className="inline-block max-w-full truncate align-middle px-2 py-0.5 rounded-full text-xs border bg-rose-50 text-rose-700 border-rose-200"
          title={t(deletionHintKey(tn.deletion))}
          data-testid={`sa-tenant-deletion-pending-${tn.tenant_id}`}
        >
          {t("tl.deletionUnfinished")}
        </span>
      )}
      {tn.deletion && !tn.deletion.committed && (
        // The other way out of an unfinished deletion (the Delete button retries it): keep
        // the practice. Beside the badge it explains, not in the fixed grid of row actions.
        // Not offered once the deletion passed its point of no return (D7 contract 3.10):
        // only the retry can finish it.
        <button
          type="button"
          onClick={() => onAct(tn.tenant_id, "abandonDeletion")}
          className="block max-w-full truncate text-xs text-slate-600 underline hover:text-slate-900"
          data-testid={`sa-tenant-abandon-deletion-${tn.tenant_id}`}
        >
          {t("tl.abandonDeletion")}
        </button>
      )}
      <div className="text-xs text-slate-400 truncate" title={`/t/${tn.slug}`}>/t/{tn.slug}</div>
    </td>
    <td className="px-3 py-4 text-slate-800/70">
      {tenantOwnerEmail(tn)
        ? <div data-cell="owner" className="truncate" title={tenantOwnerEmail(tn)}>{tenantOwnerEmail(tn)}</div>
        : <span className="text-slate-300">—</span>}
    </td>
    <td className="px-3 py-4" data-cell="plan">
      <div data-cell="plan-name" className="text-slate-700 truncate" title={tn.plan_name || tn.plan_id}>{tn.plan_name || tn.plan_id}</div>
      {billing && (
        <>
          <div data-cell="plan-price" className="text-slate-400 text-xs">
            {t(`tl.price.${billing.cycle}`, { amount: formatMoneyLedger(billing.price.cents, billing.price.currency, lang) })}
          </div>
          <div data-cell="billing-cycle" className="text-slate-400 text-xs" data-testid={`tenant-cycle-${tn.slug}`}>
            {t(`tl.cycle.${billing.cycle}`)}
          </div>
        </>
      )}
      {showCurrency && (
        <div className="text-slate-400 text-xs" data-testid={`tenant-currency-${tn.slug}`}>{String(tn.currency || "").toUpperCase()}</div>
      )}
    </td>
    <td className="px-3 py-4 text-slate-500" data-testid={`trial-${tn.slug}`}>
      {tn.trial?.state === "trialing" && Number.isInteger(tn.trial.days_remaining)
        ? t("tl.trialDays", { days: tn.trial.days_remaining })
        : "—"}
    </td>
    <td className="px-3 py-4">
      <span
        className={`inline-block max-w-full text-center align-middle px-2.5 py-1 rounded-full text-xs border ${statusBadge(tn.status)}`}
        title={t(`tl.status.${tn.status}`, undefined, tn.status)}
        data-testid={`tenant-status-${tn.slug}`}
      >
        {t(`tl.status.${tn.status}`, undefined, tn.status)}
      </span>
    </td>
    <td className="px-2 py-4">
      {/* Seven fixed slots, always in this order (contract D6 3.2.2): an action this row or
          this operator does not get leaves its slot empty, so no button ever moves into
          another's cell. Suspend and activate are exclusive and share the status slot. */}
      <div className="grid grid-cols-4 gap-0.5 w-max">
        <a
          href={`/super-admin/tenants/${tn.tenant_id}`}
          className="p-1.5 rounded-lg hover:bg-amber-100 text-amber-600"
          title={t("tl.view360")}
          aria-label={t("tl.view360")}
          data-slot="view360"
          data-testid={`view-360-${tn.slug}`}
        >
          <BarChart3 className="w-4 h-4" />
        </a>
        <a
          href={tenantSiteUrl(tn) || `/t/${tn.slug}`}
          target="_blank"
          rel="noreferrer"
          className="p-1.5 rounded-lg hover:bg-white shadow-sm text-slate-500 hover:text-slate-800"
          title={t("tl.viewSite")}
          aria-label={t("tl.viewSite")}
          data-slot="site"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
        {manageAdmin && consoleHref ? (
          <a
            href={consoleHref}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-600"
            title={t("tl.manageAdmin")}
            aria-label={t("tl.manageAdmin")}
            data-slot="manageAdmin"
            data-testid={`manage-admin-${tn.slug}`}
          >
            <LayoutDashboard className="w-4 h-4" />
          </a>
        ) : <EmptySlot slot="manageAdmin" />}
        <button
          onClick={() => onPlanEdit(tn)}
          className="p-1.5 rounded-lg hover:bg-white shadow-sm text-amber-600"
          title={t("tl.changePlan")}
          aria-label={t("tl.changePlan")}
          data-slot="plan"
          data-testid={`change-plan-${tn.slug}`}
        >
          <Package className="w-4 h-4" />
        </button>
        <button
          onClick={() => onEntitlementsEdit(tn)}
          className="p-1.5 rounded-lg hover:bg-white shadow-sm text-amber-600"
          title={t("tent.open")}
          aria-label={t("tent.open")}
          data-slot="entitlements"
          data-testid={`entitlements-${tn.slug}`}
        >
          <Settings2 className="w-4 h-4" />
        </button>
        {tn.status === "active" ? (
          <button
            onClick={() => onAct(tn.tenant_id, "suspend")}
            className="p-1.5 rounded-lg hover:bg-white shadow-sm text-amber-600"
            title={t("tl.suspend")}
            aria-label={t("tl.suspend")}
            data-slot="status"
            data-testid={`suspend-${tn.slug}`}
          >
            <Pause className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => onAct(tn.tenant_id, "activate")}
            className="p-1.5 rounded-lg hover:bg-white shadow-sm text-emerald-600"
            title={t("tl.activate")}
            aria-label={t("tl.activate")}
            data-slot="status"
            data-testid={`activate-${tn.slug}`}
          >
            <Play className="w-4 h-4" />
          </button>
        )}
        {tn.tenant_id !== "emilie" ? (
          <button
            onClick={() => onAct(tn.tenant_id, "delete")}
            className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-400"
            title={t(tn.deletion ? "tl.retryDelete" : "tl.delete")}
            aria-label={t(tn.deletion ? "tl.retryDelete" : "tl.delete")}
            data-slot="delete"
            data-testid={`delete-${tn.slug}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        ) : <EmptySlot slot="delete" />}
      </div>
    </td>
  </tr>
  );
};

export default TenantTableRow;
